import { z } from 'zod';

// ==================== BLOG TYPES ====================

export type BlogBlockType = 'PARAGRAPH' | 'IMAGE' | 'VIDEO' | 'TABLE' | 'QUOTE' | 'BUTTON';

export type BlogStatus = 'DRAFT' | 'PUBLISHED' | 'ARCHIVED';

export interface BlogAuthor {
  id: number;
  name: string;
  email: string;
  avatar: string;
  bio: string;
  position: string;
  createdAt: string;
  updatedAt: string;
}

export interface BlogCategory {
  id: number;
  name?: string;
}

export interface BlogBlock {
  id?: number;
  type: BlogBlockType;
  content: string;
  mediaUrl?: string;
  altText?: string;
  titleText?: string;
  blockOrder: number;
  _isNew?: boolean;
  _isEdited?: boolean;
  _tmpKey?: string;
}

export interface BlogSection {
  id?: number;
  title: string;
  sectionOrder: number;
  blocks: BlogBlock[];
  _isNew?: boolean;
  _isEdited?: boolean;
}

export interface Blog {
  id: number;
  title: string;
  slug: string;
  excerpt: string;
  coverImage: string;
  author: BlogAuthor;
  status: string;
  activeFooter?: boolean;
  categories: BlogCategory[];
  metaTitle: string;
  metaDescription: string;
  published: boolean;
  createdAt: string;
  updatedAt: string;
  sections: BlogSection[];
}

export interface BlogListResponse {
  content: Blog[];
  totalElements: number;
  totalPages: number;
  currentPage: number;
  size: number;
  first: boolean;
  last: boolean;
}

// ==================== REQUEST DTOs ====================

export interface BlogCreateDTO {
  title: string;
  slug: string;
  excerpt: string;
  coverImage: string;
  author: BlogAuthor;
  status: string;
  activeFooter: boolean;
  categories: BlogCategory[];
  metaTitle: string;
  metaDescription: string;
  published: boolean;
  sections: BlogSection[];
}

export interface AuthorCreateDTO {
  name: string;
  email: string;
  avatar: string;
  bio: string;
  position: string;
}

// ==================== ZOD SCHEMAS ====================

export const blogPostSchema = z.object({
  title: z.string().min(3, 'Title must be at least 3 characters').max(200, 'Title must not exceed 200 characters'),
  slug: z.string().min(3, 'Slug must be at least 3 characters').max(250, 'Slug must not exceed 250 characters'),
  excerpt: z.string().max(500, 'Excerpt must not exceed 500 characters').optional().default(''),
  coverImage: z.string().optional().default(''),
  authorId: z.number({ required_error: 'Author is required' }).min(1, 'Please select an author'),
  status: z.enum(['DRAFT', 'PUBLISHED', 'ARCHIVED']),
  categories: z.array(z.object({ id: z.number() })).default([]),
  metaTitle: z.string().max(100, 'Meta title must not exceed 100 characters').optional().default(''),
  metaDescription: z.string().max(300, 'Meta description must not exceed 300 characters').optional().default(''),
  published: z.boolean().default(false),
});

export type BlogPostFormData = z.infer<typeof blogPostSchema>;

export const authorSchema = z.object({
  name: z.string().min(2, 'Name must be at least 2 characters').max(100, 'Name must not exceed 100 characters'),
  email: z.string().email('Invalid email address'),
  avatar: z.string().optional().default(''),
  bio: z.string().max(1000, 'Bio must not exceed 1000 characters').optional().default(''),
  position: z.string().max(100, 'Position must not exceed 100 characters').optional().default(''),
});

export type AuthorFormData = z.infer<typeof authorSchema>;
